import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { FAMILY_ICONS, ruleLabel } from '../../components/common/engagementSignals';
import { apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyEngagement.css';

/* What counts, and how much — the engagement score's weights, on their own screen.

   ★ Moved off `/engagement` on 2026-09-09. It had been the second card under the roster, which
   put a settings form on the screen somebody opens to READ a number: the list of weights was
   longer than the list of people in a small company, and an admin scrolling for a colleague had
   to scroll past twenty spin boxes to reach nobody. Settings behave like settings — behind a
   button, on their own page, reached deliberately.

   ★ Saving RESCORES the company, so the button says so and the way back is the score itself: new
   weights beside yesterday's numbers is the one thing the engagement screen must never show, and
   returning to it is what makes the change visible.

   The payload is `/company/engagement`'s, unchanged — the same read the roster does. One endpoint
   for one screen's worth of state, even though this page draws half of it: a rules-only endpoint
   would be a second place for the defaults-plus-overrides merge to live.

   ★★ TWO CURRENCIES PER SIGNAL, and the second one is OFF until somebody turns it on. `points`
   is the person's engagement score — admin-only, deliberately, since a number people can see
   about themselves is a culture decision. `house_points` goes to the house the person belongs
   to, and that board hangs on the wall. Every house weight ships at 0, so the column stays
   folded away behind one switch and a company that never asked for it sees exactly the screen it
   saw yesterday.

   ⚠️ The cap is SHARED and the screen says so. How often a signal may count is a statement about
   the signal — fifty likes must not outweigh turning up — and that answer does not change with
   where the points go. A second cap would be a second place to forget. */

const EngagementRules = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);      // the weights while they are being edited
  /* Whether the house column is on show. Opens by itself when the company already scores houses,
     because a weight somebody set has to be findable without knowing to look for a switch. */
  const [showHouse, setShowHouse] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', '/company/engagement')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // ★ Only ever turns the column ON. Somebody who folded it away mid-edit must not have it
  // spring open again under them on the next save.
  useEffect(() => {
    if ((data?.rules || []).some((r) => Number(r.house_points) > 0)) setShowHouse(true);
  }, [data]);

  const act = (promise) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => { setData(res); setDraft(null); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const rules = (data && data.rules) || [];
  /* One list of weights, edited in place, drawn in two sections — the index is what a save is
     keyed on, so the sections filter and never re-order. */
  const edited = draft || rules;
  const setRule = (i, patch) => {
    const next = [...edited];
    next[i] = { ...next[i], ...patch };
    setDraft(next);
  };
  const tagRules = edited.filter((r) => r.family === 'tags');

  const ruleRow = (rule, i) => (
    <div key={rule.signal} className="eng-rule">
      <span className="eng-rule__label">
        <Icon name={FAMILY_ICONS[rule.family] || 'star'} />
        {ruleLabel(rule, t)}
        {rule.source === 'company' && (
          <span className="ce-pill ce-pill--draft">{t('ourOwn')}</span>
        )}
      </span>
      <label className="eng-rule__field">
        {t('points')}
        <input
          type="number"
          value={rule.points}
          min={-100}
          max={100}
          onChange={(e) => setRule(i, { points: e.target.value })}
        />
      </label>
      {/* ★ The house half. Bounded 0..10 by the server and by the input alike — a house total is
          a sum over a whole house, so ten times a cap of twenty times fifteen people is already
          three thousand, and a weight of 100 here would drown every other source of house
          points. Never negative: docking a whole group for what one member did is not something
          anybody asked for. */}
      {showHouse && (
        <label className="eng-rule__field">
          {t('housePointsColumn')}
          <input
            type="number"
            value={rule.house_points || 0}
            min={0}
            max={10}
            onChange={(e) => setRule(i, { house_points: e.target.value })}
          />
        </label>
      )}
      {/* No cap on a tag: somebody either carries it or does not, so it counts once and
          "at most five ambassador tags" is not a sentence about anything. */}
      {!rule.tag && (
        <label className="eng-rule__field">
          {t('countsAtMost')}
          <input
            type="number"
            value={rule.cap === null || rule.cap === undefined ? '' : rule.cap}
            min={0}
            max={1000}
            placeholder={t('noCap')}
            onChange={(e) => setRule(i, { cap: e.target.value === '' ? null : e.target.value })}
          />
        </label>
      )}
      <label className="ce-check">
        <input
          type="checkbox"
          checked={rule.is_active !== false}
          onChange={(e) => setRule(i, { is_active: e.target.checked })}
        />
        {t('counts')}
      </label>
    </div>
  );

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/engagement" />
        <Title my="1" className="mt-3">
          <Icon name="settings" theme="primary" className="me-2" />
          {t('whatCounts')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('whatCountsNote')}</p>

        {/* ★★ One switch, and the whole second currency lives behind it. Most companies score
            people and never houses; for them this screen must stay the one-number screen it has
            always been. The line under it is the only explanation anybody gets, so it says the
            three things that are actually surprising: where the points land, that membership is
            what carries them, and that the cap is shared. */}
        <label className="ce-check">
          <input
            type="checkbox"
            checked={showHouse}
            onChange={(e) => setShowHouse(e.target.checked)}
          />
          {t('alsoScoreHouses')}
        </label>
        <span className="bravos-netform__hint">{t('alsoScoreHousesHint')}</span>
        {showHouse && (
          <p className="ce-note">
            {t('housePointsNote')}
            {data.house_total > 0 && ` · ${t('housePointsTotal', { n: data.house_total })}`}
          </p>
        )}

        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      <Card>
        {edited.map((rule, i) => (rule.family === 'tags' ? null : ruleRow(rule, i)))}

        {/* The tags, apart. Not because they matter less — an ambassador may well be worth more
            than an evening — but because they are the one thing here that is our opinion of a
            person rather than a record of what they did. */}
        <Title my="1" className="mt-4">{t('tagWeights')}</Title>
        <p className="bravos-q__hint mb-2">{t('tagWeightsNote')}</p>
        {tagRules.length === 0 ? (
          <>
            <p className="ce-note">{t('noRosterTagsYet')}</p>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate('/company-members')}
            >
              <Icon name="label" /> {t('toTheRoster')}
            </button>
          </>
        ) : (
          edited.map((rule, i) => (rule.family === 'tags' ? ruleRow(rule, i) : null))
        )}

        <div className="bravos-form__row">
          {draft && (
            <>
              <button
                type="button"
                className="bravos-submit"
                disabled={busy}
                onClick={() => act(apiRequest('PUT', '/company/engagement/rules',
                                              { rules: draft }))}
              >
                {t('saveAndRescore')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setDraft(null)}>
                {t('cancel')}
              </button>
            </>
          )}
          {!draft && rules.some((r) => r.source === 'company') && (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              disabled={busy}
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (!window.confirm(t('resetWeightsConfirm'))) return;
                act(apiRequest('DELETE', '/company/engagement/rules'));
              }}
            >
              {t('resetWeights')}
            </button>
          )}
        </div>
      </Card>

      {/* The way back to the numbers these weights produce. Not a second Back — that one leaves
          the settings; this one is the point of having changed them. */}
      <Card>
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => navigate('/engagement')}
        >
          <Icon name="chart-histogram" /> {t('engagementTitle')}
        </button>
      </Card>
    </Base>
  );
};

export default EngagementRules;
